<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  suggestions: { type: Array, default: () => [] },
})
const emit = defineEmits(['send', 'stop', 'shuffle-suggestions'])

const MAX = 500
const text = ref('')
const taRef = ref(null)
const plusBtnRef = ref(null)
const quickMenuRef = ref(null)
const fileRef = ref(null)
const selectedFile = ref(null)

const count = computed(() => text.value.length)
const canSend = computed(() => !props.disabled && (text.value.trim().length > 0 || !!selectedFile.value))

/** 无文字且无附件时整体更矮 */
const isCompact = computed(
  () => !selectedFile.value && !String(text.value || '').trim(),
)

const isImageFile = computed(
  () => !!selectedFile.value?.type?.startsWith('image/'),
)
const isVideoFile = computed(
  () => !!selectedFile.value?.type?.startsWith('video/'),
)
const hasAttachedMedia = computed(
  () => !!(selectedFile.value && (isImageFile.value || isVideoFile.value)),
)

const previewUrl = ref('')

watch(selectedFile, () => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
  const f = selectedFile.value
  if (f && (f.type.startsWith('image/') || f.type.startsWith('video/')))
    previewUrl.value = URL.createObjectURL(f)
})

/** 有附件时文案区上限；纯文字时抬高上限，使整体高度可接近「缩略图+按钮+输入」的视觉高度 */
const TEXTAREA_MAX_PX = { withMedia: 140, textOnly: 232 }

function textareaMaxPx() {
  return hasAttachedMedia.value ? TEXTAREA_MAX_PX.withMedia : TEXTAREA_MAX_PX.textOnly
}

function textareaMinPx() {
  if (isCompact.value) return 30
  if (hasAttachedMedia.value) return 40
  return 38
}

function autosizeTextarea() {
  const el = taRef.value
  if (!el) return
  const max = textareaMaxPx()
  const min = textareaMinPx()
  el.style.height = '0px'
  const h = Math.min(Math.max(el.scrollHeight, min), max)
  el.style.height = `${h}px`
}

watch(
  () => [text.value, hasAttachedMedia.value, isCompact.value],
  () => nextTick(() => autosizeTextarea()),
)

function fillExplainPrompt() {
  if (!hasAttachedMedia.value || props.disabled) return
  if (!text.value.trim())
    text.value = isVideoFile.value ? '解释视频' : '解释图片'
  nextTick(() => submit())
}

function submit() {
  if (!canSend.value) return
  emit('send', {
    text: text.value.trim(),
    file: selectedFile.value
  })
  text.value = ''
  selectedFile.value = null
  closeMenu()
  quickMenuOpen.value = false
}

function stopSend() {
  emit('stop')
}

function onInput(e) {
  const v = e.target.value
  text.value = v.length > MAX ? v.slice(0, MAX) : v
  updateMenuState()
}

// ---------- @ 推荐菜单 ----------
const menuOpen = ref(false)
const menuFilter = ref('')
const menuIndex = ref(0)
const atPos = ref(-1)

function updateMenuState() {
  const ta = taRef.value
  if (!ta) return
  const cursor = ta.selectionStart
  const before = text.value.slice(0, cursor)
  const at = before.lastIndexOf('@')
  if (at < 0) return closeMenu()
  // @ 之前必须是行首或空白字符，避免误触（比如邮箱）
  const prev = at === 0 ? '' : before[at - 1]
  if (prev && !/\s/.test(prev)) return closeMenu()
  // @ 到光标之间不能有空白
  const filter = before.slice(at + 1)
  if (/\s/.test(filter)) return closeMenu()
  atPos.value = at
  menuFilter.value = filter
  if (!menuOpen.value) menuIndex.value = 0
  menuOpen.value = true
}

function closeMenu() {
  menuOpen.value = false
  menuFilter.value = ''
  atPos.value = -1
}

const filteredList = computed(() => {
  const f = menuFilter.value.trim().toLowerCase()
  if (!f) return props.suggestions
  return props.suggestions.filter(
    (s) =>
      s.text.toLowerCase().includes(f) ||
      s.tag.toLowerCase().includes(f),
  )
})

// 过滤后项目变动时把高亮归位，防止 index 越界
watch(filteredList, (list) => {
  if (menuIndex.value >= list.length) menuIndex.value = 0
})

function pickSuggestion(item) {
  if (!item) return
  text.value = ''
  closeMenu()
  emit('send', item.text)
}

function onKeydown(e) {
  if (menuOpen.value && filteredList.value.length > 0) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      menuIndex.value = (menuIndex.value + 1) % filteredList.value.length
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      menuIndex.value = (menuIndex.value - 1 + filteredList.value.length) % filteredList.value.length
      return
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      pickSuggestion(filteredList.value[menuIndex.value])
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      closeMenu()
      return
    }
  }
  // 普通回车发送（不是 Shift+Enter 换行）
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault()
    submit()
  }
}

function onClickItem(item, i) {
  menuIndex.value = i
  pickSuggestion(item)
}

function refreshSuggestions() {
  emit('shuffle-suggestions')
  menuIndex.value = 0
  nextTick(() => taRef.value?.focus())
}

// ---------- + 快捷菜单（样式对齐 ai-edu）----------
const quickMenuOpen = ref(false)
const quickActions = [
  {
    key: 'upload',
    label: '上传文件',
    icon: 'upload',
  },
  {
    key: 'research',
    label: '深入研究',
    icon: 'research',
    prompt: '请围绕下面这个主题做深入研究，并给出结构化分析（背景、要点、结论与可执行建议）：',
  },
  {
    key: 'qa',
    label: '解疑答惑',
    icon: 'note',
    prompt: '请帮我解答下面的疑问，并一步步讲清楚思路与依据：',
  },
]

function toggleQuickMenu() {
  if (props.disabled) return
  quickMenuOpen.value = !quickMenuOpen.value
}

function pickQuickAction(item) {
  quickMenuOpen.value = false
  if (item.key === 'upload') {
    nextTick(() => triggerFileSelect())
    return
  }
  if (item.prompt) {
    text.value = item.prompt
    nextTick(() => taRef.value?.focus())
  }
}

function onDocPointerDown(e) {
  if (!quickMenuOpen.value) return
  const target = e.target
  if (plusBtnRef.value?.contains(target)) return
  if (quickMenuRef.value?.contains(target)) return
  quickMenuOpen.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown)
  nextTick(() => autosizeTextarea())
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown)
  if (previewUrl.value)
    URL.revokeObjectURL(previewUrl.value)
})

function triggerFileSelect() {
  if (props.disabled) return
  fileRef.value?.click()
}

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return
  selectedFile.value = file
}

function clearFile() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
  selectedFile.value = null
  if (fileRef.value) fileRef.value.value = ''
}
</script>

<template>
  <div
    class="agent-input"
    :class="{
      'agent-input--disabled': disabled,
      'agent-input--compact': isCompact,
      'agent-input--has-media': hasAttachedMedia,
    }"
  >
    <!-- @ 推荐菜单：从输入框顶部向上弹出 -->
    <div v-if="menuOpen && filteredList.length > 0" class="at-menu" @mousedown.prevent>
      <div class="at-menu__head">
        <span>你可以这样问我</span>
        <button type="button" class="at-menu__shuffle" @click="refreshSuggestions">↻ 换一换</button>
      </div>
      <div class="at-menu__list">
        <button
          v-for="(s, i) in filteredList"
          :key="i"
          class="at-menu__item"
          :class="{ 'is-active': i === menuIndex }"
          type="button"
          @mouseenter="menuIndex = i"
          @click="onClickItem(s, i)"
        >
          <span class="at-menu__icon">{{ s.icon }}</span>
          <span class="at-menu__tag">{{ s.tag }}</span>
          <span class="at-menu__text">{{ s.text }}</span>
        </button>
      </div>
    </div>

    <div v-if="hasAttachedMedia" class="media-preview">
      <div class="media-preview__thumb">
        <img
          v-if="isImageFile && previewUrl"
          :src="previewUrl"
          alt="预览"
          class="media-preview__img"
        />
        <video
          v-else-if="isVideoFile && previewUrl"
          :src="previewUrl"
          class="media-preview__video"
          muted
          playsinline
          preload="metadata"
        />
        <button
          class="media-preview__remove"
          type="button"
          aria-label="移除附件"
          @click="clearFile"
        >
          ×
        </button>
      </div>
      <button type="button" class="media-preview__hint" @click="fillExplainPrompt">
        {{ isVideoFile ? '解释视频' : '解释图片' }} →
      </button>
    </div>

    <textarea
      ref="taRef"
      :value="text"
      rows="1"
      placeholder="您有什么想知道的，或输入 @ 选择技能来问问我"
      :disabled="disabled"
      :maxlength="MAX"
      @input="onInput"
      @keydown="onKeydown"
      @keyup="updateMenuState"
      @click="updateMenuState"
    />
    <div class="toolbar">
      <div class="count">{{ count }}/{{ MAX }}</div>
      <div class="actions">
        <input
          ref="fileRef"
          class="file-input-hidden"
          type="file"
          accept="image/*,video/*"
          @change="onFileChange" />
        <div class="quick-menu-wrap">
          <button
            ref="plusBtnRef"
            class="tool-btn tool-btn--plus"
            type="button"
            data-tip="快捷功能"
            aria-label="快捷功能"
            :aria-expanded="quickMenuOpen"
            :disabled="disabled"
            @click="toggleQuickMenu"
          >
            <span class="plus" aria-hidden="true"></span>
          </button>
          <transition name="quick-pop">
            <div v-if="quickMenuOpen" ref="quickMenuRef" class="quick-menu" @mousedown.prevent>
              <button
                v-for="item in quickActions"
                :key="item.key"
                type="button"
                class="quick-menu__item"
                @click="pickQuickAction(item)"
              >
                <span class="quick-menu__icon" aria-hidden="true">
                  <svg v-if="item.icon === 'note'" viewBox="0 0 24 24">
                    <rect x="4" y="3.5" width="16" height="17" rx="3" />
                    <path d="M8 8.5h8M8 12h8M8 15.5h5" />
                  </svg>
                  <svg v-else-if="item.icon === 'upload'" viewBox="0 0 24 24">
                    <path d="M12 4v9" />
                    <path d="M8.5 9.5 12 6l3.5 3.5" />
                    <path d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24">
                    <path
                      d="M4 11.5c0-4.2 3.2-7 8-7s8 2.8 8 7-3.2 7-8 7h-2.5L6 21v-3.5c-1.3-1.2-2-3-2-6z"
                    />
                  </svg>
                </span>
                <span class="quick-menu__label">{{ item.label }}</span>
              </button>
            </div>
          </transition>
        </div>
        <span class="divider"></span>
        <button
          class="send-btn"
          :class="disabled ? 'send-btn--stop' : { 'send-btn--active': canSend }"
          type="button"
          :data-tip="disabled ? '停止生成' : '发送'"
          :disabled="!disabled && !canSend"
          @click="disabled ? stopSend() : submit()"
        >
          <svg v-if="disabled" viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8.2" stroke="currentColor" stroke-width="1.9" />
            <rect x="9" y="9" width="6" height="6" rx="1.4" fill="currentColor" />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none">
            <path d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.agent-input {
  position: relative;
  margin: 10px 14px 0;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  padding: 10px 12px 6px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s,
    padding 0.15s ease;
}
.agent-input--compact {
  padding: 6px 10px 4px;
}
.agent-input--has-media {
  padding-top: 8px;
}
.agent-input:focus-within {
  border-color: #bcd5ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
}
.agent-input--disabled textarea { opacity: 0.75; }
.agent-input--disabled .count { opacity: 0.75; }
.agent-input--disabled .tool-btn { opacity: 0.55; }

textarea {
  display: block;
  width: 100%;
  min-height: 38px;
  max-height: 232px;
  resize: none;
  overflow-y: auto;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.55;
  color: #1f2430;
}
.agent-input--compact textarea {
  min-height: 30px;
}
.agent-input--has-media textarea {
  min-height: 40px;
  max-height: 140px;
  margin-top: 4px;
}
textarea::placeholder {
  color: #b5bac3;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}
.agent-input--compact .toolbar {
  margin-top: 2px;
}
.count {
  font-size: 12px;
  color: #b5bac3;
  user-select: none;
}
.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.divider {
  width: 1px;
  height: 16px;
  background: #e5e7eb;
}

.tool-btn {
  position: relative;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: #9aa3b2;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.tool-btn:hover { background: #f3f4f7; }
.tool-btn--plus {
  font-weight: 500;
  color: #6b7280;
}
.tool-btn--plus:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.plus {
  position: relative;
  width: 14px;
  height: 14px;
  display: inline-block;
}
.plus::before,
.plus::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  background: currentColor;
  transform: translate(-50%, -50%);
  border-radius: 999px;
}
.plus::before {
  width: 14px;
  height: 1.5px;
}
.plus::after {
  width: 1.5px;
  height: 14px;
}

.quick-menu-wrap {
  position: relative;
}
.quick-menu {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  width: max-content;
  min-width: 0;
  max-width: min(92vw, 260px);
  padding: 8px 6px;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 10px 26px rgba(17, 24, 39, 0.1);
  z-index: 30;
}
.quick-menu__item {
  width: auto;
  border: none;
  background: transparent;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: #111827;
  transition: background 0.14s ease, color 0.14s ease;
}
.quick-menu__item:hover {
  background: #f7f8fa;
}
.quick-menu__icon {
  width: 20px;
  height: 20px;
  color: #0f172a;
  flex-shrink: 0;
}
.quick-menu__icon svg {
  width: 100%;
  height: 100%;
  display: block;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.quick-menu__label {
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0;
  white-space: nowrap;
}

.quick-pop-enter-active,
.quick-pop-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.quick-pop-enter-from,
.quick-pop-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
.quick-pop-enter-to,
.quick-pop-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.file-input-hidden {
  display: none;
}

/* 输入框内：图/视频小预览 + 快捷文案 */
.media-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.media-preview__thumb {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
  flex-shrink: 0;
}
.media-preview__img,
.media-preview__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.media-preview__video {
  pointer-events: none;
}
.media-preview__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: rgba(17, 24, 39, 0.55);
  color: #fff;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.media-preview__remove:hover {
  background: rgba(17, 24, 39, 0.75);
}
.media-preview__hint {
  border: none;
  background: #eef1f5;
  color: #4b5563;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.media-preview__hint:hover {
  background: #e2e8f0;
  color: #1f2937;
}

.send-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: #f3f4f6;
  color: #c0c6d2;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: relative;
  cursor: not-allowed;
  transition: background 0.15s, color 0.15s, transform 0.15s;
}
.send-btn--active {
  background: #f3f4f6;
  color: #111827;
  cursor: pointer;
}
.send-btn--active:hover {
  background: #e5e7eb;
  color: #0f172a;
}
.send-btn--stop {
  background: #f3f4f6;
  color: #111827;
  border-radius: 999px;
  cursor: pointer;
}
.send-btn--stop:hover {
  background: #e5e7eb;
  color: #0f172a;
}
.send-btn svg { display: block; }

/* 固定在按钮正上方的提示气泡（不跟随鼠标） */
.tool-btn[data-tip]:not([aria-expanded='true']):hover::after {
  content: attr(data-tip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  padding: 7px 10px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
}
.tool-btn[data-tip]:not([aria-expanded='true']):hover::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: calc(100% + 3px);
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #111827;
  pointer-events: none;
  z-index: 5;
}

.send-btn[data-tip]:hover::after {
  content: attr(data-tip);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  padding: 7px 10px;
  border-radius: 8px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
}
.send-btn[data-tip]:hover::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: calc(100% + 3px);
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #111827;
  pointer-events: none;
  z-index: 5;
}

/* ---------- @ 推荐浮层 ---------- */
.at-menu {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #eef2f8;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(17, 24, 39, 0.12);
  overflow: hidden;
  z-index: 20;
  animation: menu-in 0.12s ease-out;
}
@keyframes menu-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.at-menu__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
  padding: 8px 12px 4px;
}
.at-menu__shuffle {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
}
.at-menu__shuffle:hover { background: #f3f4f6; color: #3b82f6; }

.at-menu__list {
  display: flex;
  flex-direction: column;
  padding: 4px;
  max-height: 260px;
  overflow-y: auto;
}
.at-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: #1f2430;
  transition: background 0.12s;
}
.at-menu__item.is-active { background: #f0f6ff; }
.at-menu__icon { font-size: 16px; flex-shrink: 0; }
.at-menu__tag {
  font-size: 12px;
  color: #3b6ad6;
  background: #e9f0ff;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 600;
}
.at-menu__text {
  flex: 1;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
